// Colour helpers for the postMailing builder. The editor stores a single
// user-chosen accent (`primary`); the supporting palette values (primaryDark,
// onPrimary, soft) are derived from it so a brand colour only has to be picked
// once and the whole letter stays visually consistent.

/**
 * Parses a #RRGGBB / #RGB string into an {r, g, b} object (0-255).
 *
 * @param {string} hex
 * @returns {{ r: number, g: number, b: number }}
 */
function hexToRgb(hex) {
	let h = String(hex || '')
		.replace('#', '')
		.trim();
	if (h.length === 3)
		h = h
			.split('')
			.map((c) => c + c)
			.join('');
	const int = parseInt(h, 16);
	return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

/**
 * Formats an {r, g, b} object (0-255, rounded) as a #RRGGBB string.
 *
 * @param {{ r: number, g: number, b: number }} rgb
 * @returns {string}
 */
function rgbToHex({ r, g, b }) {
	const to = (v) =>
		Math.max(0, Math.min(255, Math.round(v)))
			.toString(16)
			.padStart(2, '0');
	return `#${to(r)}${to(g)}${to(b)}`;
}

/**
 * Converts an {r, g, b} colour to {h, s, l} (h in degrees, s/l in 0-1).
 *
 * @param {{ r: number, g: number, b: number }} rgb
 * @returns {{ h: number, s: number, l: number }}
 */
function rgbToHsl({ r, g, b }) {
	const rn = r / 255;
	const gn = g / 255;
	const bn = b / 255;
	const max = Math.max(rn, gn, bn);
	const min = Math.min(rn, gn, bn);
	const l = (max + min) / 2;
	let h = 0;
	let s = 0;
	if (max !== min) {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		switch (max) {
			case rn:
				h = (gn - bn) / d + (gn < bn ? 6 : 0);
				break;
			case gn:
				h = (bn - rn) / d + 2;
				break;
			default:
				h = (rn - gn) / d + 4;
		}
		h *= 60;
	}
	return { h, s, l };
}

/**
 * Converts an {h, s, l} colour (h in degrees, s/l in 0-1) back to {r, g, b}.
 *
 * @param {{ h: number, s: number, l: number }} hsl
 * @returns {{ r: number, g: number, b: number }}
 */
function hslToRgb({ h, s, l }) {
	const hn = (((h % 360) + 360) % 360) / 360;
	if (s === 0) {
		const v = l * 255;
		return { r: v, g: v, b: v };
	}
	const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
	const p = 2 * l - q;
	const hue = (t) => {
		let tn = t;
		if (tn < 0) tn += 1;
		if (tn > 1) tn -= 1;
		if (tn < 1 / 6) return p + (q - p) * 6 * tn;
		if (tn < 1 / 2) return q;
		if (tn < 2 / 3) return p + (q - p) * (2 / 3 - tn) * 6;
		return p;
	};
	return { r: hue(hn + 1 / 3) * 255, g: hue(hn) * 255, b: hue(hn - 1 / 3) * 255 };
}

/**
 * Relative luminance (0-1) of an {r, g, b} colour, per WCAG. Used to pick a
 * legible foreground colour for a given background.
 *
 * @param {{ r: number, g: number, b: number }} rgb
 * @returns {number}
 */
function luminance({ r, g, b }) {
	const ch = (c) => {
		const cn = c / 255;
		return cn <= 0.03928 ? cn / 12.92 : ((cn + 0.055) / 1.055) ** 2.4;
	};
	return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
}

/**
 * Returns black or white — whichever reads better on the given background.
 *
 * @param {string} hex - Background colour.
 * @returns {string} '#ffffff' or '#111111'
 */
export function readableTextColor(hex) {
	return luminance(hexToRgb(hex)) > 0.5 ? '#111111' : '#ffffff';
}

/**
 * Derives the full accent palette from a single primary colour. primaryDark is
 * a darkened shade for gradients/depth, soft is a very light tint for block
 * backgrounds, and onPrimary is the legible foreground on the primary colour.
 *
 * @param {string} primary - User-chosen accent colour (#RRGGBB).
 * @returns {{ primary: string, primaryDark: string, onPrimary: string, soft: string }}
 */
export function derivePalette(primary) {
	const hsl = rgbToHsl(hexToRgb(primary));
	const primaryDark = rgbToHex(hslToRgb({ h: hsl.h, s: Math.min(1, hsl.s + 0.1), l: Math.max(0.12, hsl.l * 0.4) }));
	const soft = rgbToHex(hslToRgb({ h: hsl.h, s: Math.min(hsl.s, 0.5), l: 0.93 }));
	return {
		primary,
		primaryDark,
		onPrimary: readableTextColor(primary),
		soft,
	};
}
